// ─────────────────────────────────────────────────────────────────
// Columns block — column widths
// ----------------------------------------------------------------
// A `columns` block is a row: by default every column is an equal fraction of
// it, but a row often wants one column pinned (a logo, an avatar, a price) and
// the rest to share whatever is left. `attrs.columnWidths` holds that pin,
// index-aligned with `attrs.columnBlocks`.
//
// Only the two lengths an email table can be trusted with are accepted —
// pixels (Word's engine honours the width attribute and the inline style) and
// percentages — and this module is the single rule the editor inspector and
// the theme renderer both judge a value by, so what the panel accepts is
// exactly what the email emits.
//
// Dependency-free on purpose: themes/minimal/renderer.js is a bundle that
// inlines whatever it imports, so a row must not drag the block schemas' zod
// dependency in with it.
// ─────────────────────────────────────────────────────────────────

/** A pinned length: `160px` or `30%`. */
export const COLUMN_WIDTH_PATTERN = /^(\d+(?:\.\d+)?)(px|%)$/;

/**
 * The width one column is pinned to, or `null` when it takes the rest of the
 * row. Index-aligned with `columnBlocks`; a missing, empty, malformed or
 * impossible value reads as "fill", so a hand-edited or imported block can
 * never emit junk into a style attribute or overflow the table.
 */
export function parseColumnWidth(raw: unknown, contentWidth: number): string | null {
  if (typeof raw !== 'string') return null;

  const match = COLUMN_WIDTH_PATTERN.exec(raw.trim());
  if (!match) return null;

  const value = Number(match[1]);
  const unit = match[2];
  if (!Number.isFinite(value) || value <= 0) return null;

  // A column that is as wide as the row leaves nothing for the columns that
  // fill it, which is the opposite of what the pin is for.
  if (unit === '%') return value < 100 ? `${value}%` : null;
  return value < contentWidth ? `${value}px` : null;
}
